import type { Option } from '@branchjs/neo-components';
import type { ACCOUNT_VALIDATION_KEYS } from '../../../../../../shared/constants/accountValidationConstants';

/**
 * An object containing error messages for each form field.
 * @interface
 */
export interface ErrorFootnotes {
  /**
   * Validation message for the user's first name.
   * @type {(string|null)}
   */
  [ACCOUNT_VALIDATION_KEYS.NAME]: string | null;
  /**
   * Validation message for the user's last name.
   * @type {(string|null)}
   */
  [ACCOUNT_VALIDATION_KEYS.LAST_NAME]: string | null;
  /**
   * Validation message for the user's email address.
   * @type {(string|null)}
   */
  [ACCOUNT_VALIDATION_KEYS.EMAIL]: string | null;
  /**
   * Validation message for the user's password.
   * @type {(string|null)}
   */
  [ACCOUNT_VALIDATION_KEYS.PASSWORD]: string | null;
  /**
   * Validation message for the user's password confirmation.
   * @type {(string|null)}
   */
  [ACCOUNT_VALIDATION_KEYS.CONFIRM_PASSWORD]: string | null;
  /**
   * Validation message for the user's role.
   * @type {(string|null)}
   */
  [ACCOUNT_VALIDATION_KEYS.ROLE]: string | null;
  /**
   * Validation message (or state) regarding user accepting the terms and conditions.
   * @type {(string|boolean|null)}
   */
  [ACCOUNT_VALIDATION_KEYS.TERMS]: string | boolean | null;
  /**
   * Validation message for user's company name.
   * @type {(string|null)}
   */
  [ACCOUNT_VALIDATION_KEYS.COMPANY_NAME]: string | null;
  /**
   * Validation message for user's company website.
   * @type {(string|null)}
   */
  [ACCOUNT_VALIDATION_KEYS.COMPANY_URL]: string | null;
}

/**
 * The state of the SignupForm component.
 * @interface
 */
export interface SignupState {
  /**
   * Any errors that occur during form submission.
   * @type {any}
   */
  errors: any;
  /**
   * The user's first name.
   * @type {(string|null)}
   */
  name: string | null;
  /**
   * The user's last name.
   * @type {(string|null)}
   */
  last_name: string | null;
  /**
   * The user's email address.
   * @type {(string|null)}
   */
  email: string | null;
  /**
   * The user's password.
   * @type {(string|null)}
   */
  password: string | null;
  /**
   * The user's password confirmation.
   * @type {(string|null)}
   */
  password2: string | null;
  /**
   * The user's role.
   * @type {(string|null)}
   */
  role: string | null;
  /**
   * The user's company name.
   * @type {(string|null)}
   */
  company_name: string | null;
  /**
   * The user's company website.
   * @type {(string|null)}
   */
  company_url: string | null;
  /**
   * Whether the user has accepted the terms and conditions.
   * @type {boolean}
   */
  acceptedTerms: boolean;
  /**
   * Whether the user has opted out of marketing communications.
   * @type {(boolean|undefined)}
   */
  marketingOptOut?: boolean;
  /**
   * Whether the password contains lowercase characters.
   * @type {boolean}
   */
  lowercase: boolean;
  /**
   * Whether the password contains capital letters.
   * @type {boolean}
   */
  capital: boolean;
  /**
   * Whether the password contains numbers.
   * @type {boolean}
   */
  number: boolean;
  /**
   * Whether the password contains symbols.
   * @type {boolean}
   */
  symbol: boolean;
  /**
   * Whether the password meets the minimum length requirement.
   * @type {boolean}
   */
  min: boolean;
  /**
   * Whether the two password fields match.
   * @type {boolean}
   */
  passwordMatch: boolean;
  /**
   * Whether the password field has been checked for validity.
   * @type {boolean}
   */
  passwordChecked: boolean;
  /**
   * Whether the password2 field has been checked for validity.
   * @type {boolean}
   */
  password2Checked: boolean;
  /**
   * An object containing error messages for each form field.
   * @type {ErrorFootnotes}
   */
  errorFootnotes: ErrorFootnotes;
}

export const RoleOptions: Option[] = [
  { value: 'Marketer', label: 'Marketing' },
  { value: 'Product Manager', label: 'Product' },
  { value: 'Business Intelligence', label: 'Business Intelligence' },
  { value: 'Developer', label: 'Engineering' },
  { value: 'IT Administration', label: 'IT Administration' },
  { value: 'Agency', label: 'Agency' },
  { value: 'Finance', label: 'Finance' },
  { value: 'Other', label: 'Other' },
];
